
import { Box, Button, ButtonGroup, Checkbox, Chip, FormControlLabel, FormGroup, Grid, Snackbar, TextField, Typography, } from '@mui/material';
import { AppModels, LabelConfiguration, LabelItem, LabelItemType, adjustConfigToUserDefinedDimensions, hasErrors, labelEditorSlice, printLabelSlice, saveModel } from 'cdx-common';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState, appStore } from 'store';
import '../../../assets/labeleditor.css';
import { LabelerPage } from './LabelerPage';
import { LabelEditingRenderer } from './labels/LabelRenderer';
import { LabelItemPropsEditor } from './labels/labelitempropseditor';

export const LabelEditor = () => {

    const currentLabel = useSelector((state: RootState) => state.labelEditor.currentLabel);

    React.useEffect(() => {
        appStore.dispatch(labelEditorSlice.actions.updateStuff());
    }, []);
    React.useEffect(() => {
        localStorage.setItem('currentLabel', JSON.stringify(currentLabel));
    }, [currentLabel]);

    return <><Grid container spacing={2}>
        <Grid item xs={6}>
            <Box margin={2}>
                <SavePrintLabelSection />
            </Box>
            <Box margin={2}>
                <LabelEditorTopControls />
            </Box>
            <div className='label-page-background' style={{ padding: '1em' }}>
                <LabelEditingRenderer />
            </div>

        </Grid>
        <Grid item xs={6}>
            <LabelItemPropsEditor />
        </Grid>
        <GroupingControls />
    </Grid >
        <div>
            <p>Apperçu de l'impression</p>
            <LabelerPage offset={0} />
        </div>
        <Box sx={{ textAlign: 'center' }}><PrintButton /></Box>

    </>;
};


const SavePrintLabelSection = () => {


    const currentLabel = useSelector((state: RootState) => state.labelEditor.currentLabel);
    const [message, setMessage] = React.useState('');


    if (!currentLabel) return <></>;


    const onSaveNew = async () => {
        if (currentLabel) {
            const { id, ...rest } = currentLabel;
            const response = await saveModel(AppModels.printLabels, { ...rest, id: '0' });
            if (!hasErrors(response)) {
                appStore.dispatch(printLabelSlice.actions.upsertOne(response));
                setMessage('Une nouvelle copie a été créée');
            }
            appStore.dispatch(labelEditorSlice.actions.setCurrentPrintLabel(response));
        }
    };
    const onSave = async () => {
        if (currentLabel) {
            const response = await saveModel(AppModels.printLabels, currentLabel);
            if (!hasErrors(response)) {
                appStore.dispatch(printLabelSlice.actions.upsertOne(response));
                setMessage('Étiquette sauvegardée');
            }
            appStore.dispatch(labelEditorSlice.actions.setCurrentPrintLabel(response));
        }
    };

    const onNewLabel = () => {
        appStore.dispatch(labelEditorSlice.actions.setCurrentPrintLabel(undefined));
    };

    return <><Grid container spacing={1}>
        <Grid item xs={12}>
            <Button variant='outlined' onClick={onNewLabel}>Nouvelle étiquette</Button>
        </Grid>
        <Grid item xs={12}>
            <FormGroup row>


                <TextField
                    sx={{ m: 1 }}
                    size='small'
                    label='Nom du gabarit'
                    value={currentLabel.name}
                    onChange={(event) => {
                        appStore.dispatch(labelEditorSlice.actions.updatePrintLabel({ name: event.target.value }));
                    }}
                />
                <Button sx={{ m: 1 }} variant='contained' size='small' onClick={onSave}>Sauvegarder</Button>
                {currentLabel.id ? <Button sx={{ m: 1 }} variant='contained' size='small' onClick={onSaveNew}> Nouvelle copie</Button> : null}
                {currentLabel.__errors ? <Typography color='red'>Erreur. Impossible de sauvegarder le gabarit.</Typography> : null}
            </FormGroup>
        </Grid>
    </Grid>
        <Snackbar
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            open={!!message}
            onClose={() => { setMessage(''); }}
            message={message}
            key={message}
        />
    </>;
};

const GroupingControls = () => {

    const currentLabel = useSelector((state: RootState) => state.labelEditor.currentLabel);


    if (!currentLabel) return <></>;

    const toggleGrouping = (index: number, groupable: boolean) => {
        appStore.dispatch(labelEditorSlice.actions.updatePrintLabelItemAttrs({
            index,
            attrs: {
                groupable
            },
            updateConfiguration: true
        }));
    };



    return <><Grid item xs={12}>
        Rassembler les étiquettes de valeurs communes
        <FormGroup>
            {currentLabel.items.map((p, index: number) => {
                if (p.type != LabelItemType.STATIC_TEXT) {
                    return <FormControlLabel key={'groupcontrol' + index} onChange={(_event, checked) => {
                        toggleGrouping(index, checked);
                    }} control={<Checkbox checked={p.groupable === true} />} label={p.mapping}></FormControlLabel>;
                } else return null;
            })}
        </FormGroup>
        <hr />
        <FormGroup>
            <FormControlLabel control={<Checkbox onChange={(_e, checked) => {
                appStore.dispatch(labelEditorSlice.actions.updatePrintLabelConfiguration({
                    orhanLabelsAtEndJob: checked
                }));
            }} checked={currentLabel.configuration.orhanLabelsAtEndJob === true} />} label="Rassembler les orphelins en fin d'impression"></FormControlLabel>
        </FormGroup>
    </Grid></>;
};


const PrintButton = () => {




    const currentLabel = useSelector((state: RootState) => state.labelEditor.currentLabel);
    if (!currentLabel) return <></>;
    const navigate = useNavigate();

    const doPrint = () => {
        navigate('/labeler/print/');
    };

    return <Button size='large' variant='contained' onClick={() => {
        doPrint();
    }}>Imprimer</Button>;
};

const LabelEditorTopControls = () => {

    const currentLabel = useSelector((state: RootState) => state.labelEditor.currentLabel);
    const labelSizeInPixels = useSelector((state: RootState) => state.labelEditor.labelSizeInPixels) ?? { width: 40 * 2, height: 12 * 5 };

    const [width, setWidth] = React.useState<string | undefined>(currentLabel?.configuration.userDefinedDimensions?.width + '');
    const [height, setHeight] = React.useState<string | undefined>(currentLabel?.configuration.userDefinedDimensions?.height + '');

    if (!currentLabel) return <></>;

    const addStatic = () => {
        if (!labelSizeInPixels) return;
        const w: number = Math.round((labelSizeInPixels.width / 2));
        const h: number = Math.round((labelSizeInPixels.height / 5));
        const x: number = Math.round(Math.random() * (labelSizeInPixels.width - w));
        const y: number = Math.round(Math.random() * (labelSizeInPixels.height - h));
        const newLabel: LabelItem = {
            type: LabelItemType.STATIC_TEXT,
            props: {
                width: w,
                height: h,
                y: y,
                x: x,
                fontSize: h - 1,
            },
            value: 'Texte ' + (currentLabel.items.length + 1)
        };
        appStore.dispatch(labelEditorSlice.actions.addLabelItem(newLabel));
    };
    const addDynamic = () => {
        if (!labelSizeInPixels) return;
        const w: number = Math.round((labelSizeInPixels?.width / 2));
        const h: number = Math.round((labelSizeInPixels?.height / 5));
        const x: number = Math.round(Math.random() * (labelSizeInPixels.width - w));
        const y: number = Math.round(Math.random() * (labelSizeInPixels.height - h));
        const newLabel: LabelItem = {
            type: LabelItemType.DYNAMIC_TEXT,
            props: {
                width: w,
                height: h,
                y: y,
                x: x,
                fontSize: h - 1,
            },
            value: 'Texte ' + (currentLabel.items.length + 1),
            mapping: 'Colonne' + (currentLabel.items.length + 1)
        };
        appStore.dispatch(labelEditorSlice.actions.addLabelItem(newLabel));
    };
    const addBarCode = () => {
        if (!labelSizeInPixels) return;
        const w: number = Math.round((labelSizeInPixels?.width / 2));
        const h: number = Math.round((labelSizeInPixels?.height / 5));
        const x: number = Math.round(Math.random() * (labelSizeInPixels.width - w));
        const y: number = Math.round(Math.random() * (labelSizeInPixels.height - h));
        const newLabel: LabelItem = {
            type: LabelItemType.BARCODE,
            props: {
                width: w,
                height: h,
                y: y,
                x: x,
                fontSize: h - 1,
            },
            value: '1234567890',
            mapping: 'Colonne' + (currentLabel.items.length + 1)
        };

        appStore.dispatch(labelEditorSlice.actions.addLabelItem(newLabel));
    };
    const addQrCode = () => {
        if (!labelSizeInPixels) return;
        const w: number = Math.round((labelSizeInPixels.height * 0.75));
        const h: number = w;
        const x: number = Math.round(Math.random() * (labelSizeInPixels.width - w));
        const y: number = Math.round(Math.random() * (labelSizeInPixels.height - h));
        const newLabel: LabelItem = {
            type: LabelItemType.QRCODE,
            props: {
                width: w,
                height: h,
                y: y,
                x: x,
                fontSize: 12,
            },
            value: undefined,
            mapping: 'Colonne' + (currentLabel.items.length + 1)
        };

        appStore.dispatch(labelEditorSlice.actions.addLabelItem(newLabel));
    };

    React.useEffect(() => {
        const val = parseFloat(width + '');
        const currConfig: LabelConfiguration = { ...currentLabel.configuration };
        if (val && currConfig.userDefinedDimensions && val >= 1 && val <= currentLabel.template.paper.printableArea.width) {

            appStore.dispatch(labelEditorSlice.actions.updatePrintLabelConfiguration(
                adjustConfigToUserDefinedDimensions(
                    {
                        ...currConfig.userDefinedDimensions,
                        width: val
                    },
                    currConfig,
                    currentLabel.template
                )
            ));
        }
    }, [width]);
    React.useEffect(() => {
        const val = parseFloat(height + '');
        const currConfig: LabelConfiguration = { ...currentLabel.configuration };
        if (val && currConfig.userDefinedDimensions && val >= 1 && val <= currentLabel.template.paper.printableArea.height) {

            appStore.dispatch(labelEditorSlice.actions.updatePrintLabelConfiguration(
                adjustConfigToUserDefinedDimensions(
                    {
                        ...currConfig.userDefinedDimensions,
                        height: val
                    },
                    currConfig,
                    currentLabel.template
                )
            ));
        }
    }, [height]);

    return <Grid container spacing={1}>
        {currentLabel.configuration.userDefinedDimensions ? <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'left', gap: 1 }} alignContent='center'>
            <TextField
                type='number'
                label="Largeur"
                inputProps={{
                    step: '0.1'
                }}
                sx={{ width: 100 }}
                error={parseFloat(width + '') != currentLabel.configuration.userDefinedDimensions.width}
                value={width}
                onChange={event => setWidth(event.target.value)} />
            <TextField
                type='number'
                sx={{ width: 100 }}
                error={parseFloat(height + '') != currentLabel.configuration.userDefinedDimensions.height}
                label="Hauteur"
                inputProps={{
                    step: '0.1'
                }}
                value={height}
                onChange={event => setHeight(event.target.value)} />
            <Chip label={currentLabel.configuration.labelCountHorizontally + ' x ' + currentLabel.configuration.labelCountVertically + ' (' + (currentLabel.configuration.labelCountHorizontally * currentLabel.configuration.labelCountVertically) + ' par feuille)'} />
        </Grid> : null}
        <Grid item xs={12}>
            <ButtonGroup>
                <Button variant='outlined' onClick={addStatic} title='Texte statique'>Statique</Button>
                <Button variant='outlined' onClick={addDynamic} title="Texte dynamique, provenant de la colonne d'une source de données">Dynamique</Button>
                <Button variant='outlined' onClick={addBarCode}>BARCODE</Button>
                <Button variant='outlined' onClick={addQrCode}>Code QR</Button>
            </ButtonGroup>
        </Grid>
    </Grid>;
};
